// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { MarginTypes } from '@shared/utils/Instruments/MarginTypes';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { RiskPlanDataKey } from '@shared/utils/RiskPlan/RiskPlanDataKey';
import { RiskPlanMargin } from '@shared/utils/RiskPlan/RiskPlanMargin';
import { RiskPlanSettings } from './RiskPlanSettings';
export class RiskPlan {
    constructor() {
        this.Id = 0;
        this.Name = null;
        this.riskPlanCache = new RiskPlanCache();
        this.items = {};
    }
    update(dataCache, message) {
        this.Id = message.Id;
        if (message.Name) {
            this.Name = message.Name;
        }
        const riskPlanCache = this.riskPlanCache;
        const data = message.Data;
        for (let i = 0, len = data.length; i < len; i++) {
            riskPlanCache.addRiskData(data[i]);
        }
        // TODO.
        const instruments = dataCache.getCachedInstrumentDict();
        const newItems = {};
        for (const key in instruments) {
            this.processOneInstrument(instruments[key], newItems);
        }
        this.items = newItems;
    }
    applyToInstrument(dataCache) {
        for (const it in this.items) {
            const item = this.items[it];
            const instrument = dataCache.getInstrumentByName(it);
            if (instrument == null) {
                continue;
            }
            instrument.RiskSettings.RiskPlanSettings = item;
            instrument.RiskSettingsUpdated.Raise();
        }
    }
    processOneInstrument(instrument, newItems) {
        newItems = newItems || this.items;
        const item = this.riskPlanCache.getRiskPlanSettings(instrument);
        newItems[instrument.GetInteriorID()] = item;
        // TODO. Use current acc risk plan.
        instrument.RiskSettings.RiskPlanSettings = item;
        instrument.RiskSettingsUpdated.Raise();
    }
    GetRisksForInstrument(instrumentName) {
        let item = this.items[instrumentName];
        if (!item) {
            item = new RiskPlanSettings();
        }
        return item;
    }
    // margin
    static GetRiskPlanItem(instrument, productType) {
        const rs = instrument.RiskSettings;
        const rps = rs.RiskPlanSettings;
        const ri = rps.get(productType);
        return ri;
    }
    // GetMargin
    static GetMargin(instrument, productType, tier) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (isNullOrUndefined(ri)) {
            return new RiskPlanMargin(0, 0, 0);
        }
        if (RiskPlan.IsPriceBasedMultipleCoefMarginType(instrument, productType)) {
            const margin = new RiskPlanMargin();
            margin.Leverage = ri.GetLeverageTier(tier);
            return margin;
        } // #113782 comments
        return new RiskPlanMargin(ri.GetWarnMargin(tier), ri.GetInitMargin(tier), ri.GetMaintMargin(tier));
    }
    static GetMarginCoeficientListLength(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (!isValidArray(ri === null || ri === void 0 ? void 0 : ri.MarginCoeficientList)) {
            return 0;
        }
        if (!RiskPlan.IsTierMargin(instrument, productType)) {
            // #95613   подозреваю баг на сервере из-за которого присылаются лишние коэф.
            return 1;
        } // несколько слоев коэф. может быть только у TierMargin, у остальных 1
        const list = ri.MarginCoeficientList;
        return list.length > 0 ? list.length : 0;
    }
    static IsTierMargin(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (isNullOrUndefined(ri)) {
            return false;
        }
        const marginType = ri.GetMarginType();
        return (marginType === MarginTypes.TIERED_PRICE_BASED_CUSTOM_COEF ||
            marginType === MarginTypes.TIERED_PRICE_BASED_CUSTOM_COEF_WITH_RANGES);
    }
    static GetOvernightMargin(instrument, productType, tier) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (isNullOrUndefined(ri)) {
            return new RiskPlanMargin(0, 0, 0);
        }
        return new RiskPlanMargin(ri.GetWarnMarginOvernight(tier), ri.GetInitMarginOvernight(tier), ri.GetMaintMarginOvernight(tier));
    }
    static GetShortMargin(instrument, productType, tier) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (isNullOrUndefined(ri)) {
            return new RiskPlanMargin(0, 0, 0);
        }
        return new RiskPlanMargin(ri.GetWarnMarginShort(tier), ri.GetInitMarginShort(tier), ri.GetMaintMarginShort(tier));
    }
    static GetOvernightShortMargin(instrument, productType, tier) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (isNullOrUndefined(ri)) {
            return new RiskPlanMargin(0, 0, 0);
        }
        return new RiskPlanMargin(ri.GetWarnMarginOvernightShort(tier), ri.GetInitMarginOvernightShort(tier), ri.GetMaintMarginOvernightShort(tier));
    }
    // UseMargin
    static UseOvernightMargin(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (isNullOrUndefined(ri)) {
            return false;
        }
        return ri.GetUseOvernightMargin();
    }
    static UseLongShortMargin(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (isNullOrUndefined(ri)) {
            return false;
        }
        return ri.GetUseLongShortMargin();
    }
    // CoeffNullable
    static GetUseVarOnlyIntraday(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (isNullOrUndefined(ri)) {
            return false;
        }
        const varCoeff = ri.GetUseVarOnlyIntraday();
        return isValidNumber(varCoeff) && varCoeff !== 0;
    }
    static GetVarCoeffNullable(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (!ri) {
            return null;
        }
        return ri.GetVarCoeff();
    }
    static GetVarCoeffApplyingNullable(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (!ri) {
            return null;
        }
        return ri.GetVarCoeffApplying();
    }
    // GetType
    static GetCalculateMarginTypeNullable(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (!ri) {
            return null;
        }
        return ri.GetMarginType();
    }
    // ProductTypes
    static GetAvailableProductTypes(account, instrument) {
        if (!isNullOrUndefined(account) && !isNullOrUndefined(account.RiskPlan)) {
            return account.RiskPlan.GetRisksForInstrument(instrument.GetInteriorID()).availableProductTypes;
        }
        else {
            return instrument.RiskSettings.RiskPlanSettings.availableProductTypes;
        }
    }
    static AvailableOnlyOneProductType(instrument) {
        return instrument.RiskSettings.RiskPlanSettings.availableProductTypes.length === 1;
    }
    static IsProductTypeAvailableForInstrument(instrument, productType) {
        const availableProductTypes = instrument.RiskSettings.RiskPlanSettings.availableProductTypes;
        for (let i = 0; i < availableProductTypes.length; i++) {
            if (availableProductTypes[i] === productType) {
                return true;
            }
        }
        return false;
    }
    // Leverages #111414->#112880->#112886
    static GetLeverages(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (isNullOrUndefined(ri)) {
            return [];
        }
        return ri.GetLeverages();
    }
    static IsPriceBasedMultipleCoefMarginType(instrument, productType) {
        var _a, _b;
        return (_b = (_a = RiskPlan.GetRiskPlanItem(instrument, productType)) === null || _a === void 0 ? void 0 : _a.IsPriceBasedMultipleCoefMarginType()) !== null && _b !== void 0 ? _b : false;
    }
    static IsNoneMarginType(instrument, productType) {
        var _a, _b;
        return (_b = (_a = RiskPlan.GetRiskPlanItem(instrument, productType)) === null || _a === void 0 ? void 0 : _a.IsNoneMarginType()) !== null && _b !== void 0 ? _b : false;
    }
    static IsLeverageVisible(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (!ri) {
            return false;
        }
        return ri.IsLeverageVisible();
    }
    static GetCurrencyId(instrument, productType) {
        const ri = RiskPlan.GetRiskPlanItem(instrument, productType);
        if (!ri) {
            return null;
        }
        return ri.GetCurrencyId();
    }
    static GetCustomCurrencyStr(instrument, productType) {
        var _a;
        // #116496
        const currencyId = RiskPlan.GetCurrencyId(instrument, productType);
        let currencyStr = '';
        if (currencyId === null) {
            return currencyStr;
        }
        const instrumentBaseCCY = -1;
        const instrumentQuotingCCY = -2;
        if (currencyId === instrumentBaseCCY) {
            currencyStr = instrument.Exp1;
        }
        else if (currencyId === instrumentQuotingCCY) {
            currencyStr = instrument.Exp2;
        }
        else {
            currencyStr = (_a = instrument.DataCache.GetAssetById(currencyId)) === null || _a === void 0 ? void 0 : _a.Name;
        }
        return currencyStr;
    }
    GetUseSettledCashForStocksForOpenedPositionsInMA() {
        if (!this.riskPlanCache)
            return null;
        const riskPlanSettings = this.riskPlanCache.getDefaultRiskPlanSettings();
        if (!riskPlanSettings)
            return null;
        const productType = riskPlanSettings.availableProductTypes
            ? riskPlanSettings.availableProductTypes[0]
            : ProductType.General.toString();
        const riskItem = riskPlanSettings.get(productType);
        return riskItem ? riskItem.GetUseSettledCashForStocksForOpenedPositionsInMA() : null;
    }
    GetUseSettledCollateralForOpenedPositionInMA() {
        if (!this.riskPlanCache)
            return null;
        const riskPlanSettings = this.riskPlanCache.getDefaultRiskPlanSettings();
        if (!riskPlanSettings)
            return null;
        const productType = riskPlanSettings.availableProductTypes
            ? riskPlanSettings.availableProductTypes[0]
            : ProductType.General.toString();
        const riskItem = riskPlanSettings.get(productType);
        return riskItem ? riskItem.GetUseSettledCollateralForOpenedPositionInMA() : null;
    }
}
RiskPlan.showProductType = false;
export class RiskPlanCache {
    constructor() {
        this.defaultRiskSettings = new RiskPlanSettings();
        this.defaultRiskDataCache = [];
        this.riskPlanDataDictionary = new Map();
    }
    addRiskData(data) {
        if (!data)
            return;
        const defaultRiskSettings = this.defaultRiskSettings;
        const defaultRiskDataCache = this.defaultRiskDataCache;
        // let chainByRoute = this.chainByRoute #85526
        if (!data.RiskPlanDataKey.Route) {
            defaultRiskSettings.addItem(data.Item);
            defaultRiskDataCache.push(data);
            // Костя: сервер не гарантирует порядок настроек.
            // Добавляем дефолт дату в уже пришедшие настройки по роутам
            // for (let key in chainByRoute)  #85526
            //     chainByRoute[key].addRiskData(data)
            return;
        }
        const itemKey = JSON.stringify(data.RiskPlanDataKey);
        let riskPlanSettings = this.riskPlanDataDictionary.get(itemKey);
        if (isNullOrUndefined(riskPlanSettings)) {
            riskPlanSettings = new RiskPlanSettings();
            this.riskPlanDataDictionary.set(itemKey, riskPlanSettings);
        }
        riskPlanSettings.addItem(data.Item);
    }
    getDefaultRiskPlanSettings() {
        return this.defaultRiskSettings;
    }
    checkItem(cacheItem, ProductType, FieldLiteral) {
        if (!cacheItem) {
            return false;
        }
        if ((ProductType === null && ProductType === undefined) || !FieldLiteral) {
            return true;
        }
        if (cacheItem.get(ProductType) && cacheItem.get(ProductType)[FieldLiteral] !== null) {
            return true;
        }
        return false;
    }
    getRiskPlanSettings(instrument, ProductType = undefined, FieldLiteral = undefined) {
        if (this.riskPlanDataDictionary.size === 0 || !instrument) {
            return this.defaultRiskSettings;
        }
        let cacheItem;
        // 1 Всегда пробуем создать ключ для конкретного инструмента
        let riskPlanDataKey = new RiskPlanDataKey();
        riskPlanDataKey.TradableId = instrument.InstrumentTradableID;
        riskPlanDataKey.Route = instrument.Route;
        cacheItem = this.riskPlanDataDictionary.get(JSON.stringify(riskPlanDataKey));
        if (this.checkItem(cacheItem, ProductType, FieldLiteral)) {
            return cacheItem;
        }
        if (instrument.InstrType === InstrumentTypes.OPTIONS) {
            riskPlanDataKey = new RiskPlanDataKey();
            riskPlanDataKey.ContractId = instrument.ContractID;
            riskPlanDataKey.Route = instrument.Route;
            cacheItem = this.riskPlanDataDictionary.get(JSON.stringify(riskPlanDataKey));
            if (this.checkItem(cacheItem, ProductType, FieldLiteral)) {
                return cacheItem;
            }
        }
        if (instrument.InstrType === InstrumentTypes.OPTIONS || instrument.InstrType === InstrumentTypes.FUTURES) {
            riskPlanDataKey = new RiskPlanDataKey();
            riskPlanDataKey.InstrumentId = instrument.Id;
            riskPlanDataKey.Route = instrument.Route;
            cacheItem = this.riskPlanDataDictionary.get(JSON.stringify(riskPlanDataKey));
            if (this.checkItem(cacheItem, ProductType, FieldLiteral)) {
                return cacheItem;
            }
        }
        const resForGroups = this.GetRiskFeatureForGroup(instrument.DataCache, instrument.TypeId, instrument.Route);
        if (resForGroups) {
            return resForGroups;
        }
        riskPlanDataKey = new RiskPlanDataKey();
        riskPlanDataKey.InstrumentGroupId = instrument.TypeId;
        riskPlanDataKey.Route = instrument.Route;
        cacheItem = this.riskPlanDataDictionary.get(JSON.stringify(riskPlanDataKey));
        if (this.checkItem(cacheItem, ProductType, FieldLiteral)) {
            return cacheItem;
        }
        riskPlanDataKey = new RiskPlanDataKey();
        riskPlanDataKey.Route = instrument.Route;
        cacheItem = this.riskPlanDataDictionary.get(JSON.stringify(riskPlanDataKey));
        if (this.checkItem(cacheItem, ProductType, FieldLiteral)) {
            return cacheItem;
        }
        return this.defaultRiskSettings;
    }
    GetRiskFeatureForGroup(dataCache, instrumentGroupId, routeName) {
        const riskPlanDataKey = new RiskPlanDataKey();
        riskPlanDataKey.InstrumentGroupId = instrumentGroupId;
        riskPlanDataKey.Route = routeName;
        const cacheItem = this.riskPlanDataDictionary.get(JSON.stringify(riskPlanDataKey));
        if (cacheItem) {
            return cacheItem;
        }
        const instType = dataCache.getInstrumentTypeById(instrumentGroupId);
        if (instType == null || instType.SuperTypeId === -1) {
            return null;
        }
        return this.GetRiskFeatureForGroup(dataCache, instType.SuperTypeId, routeName);
    }
}
